import type {
  AlignmentValue,
  CollectionItem,
  DateTimeValue,
  FBPageValue,
  FontValue,
  ImageValue,
  LinkValue,
  MulticolorValue
} from '@elfsight/configurator-contracts';
import type { GoogleAnalyticsEventValue } from '@elfsight/lib-google-analytics';
import type { WidgetSettings } from '@wu/widget-core';

export enum ChatsProvider {
  all = 'all',
  whatsapp = 'whatsapp',
  telegram = 'telegram',
  instagram = 'instagram',
  viber = 'viber',
  line = 'line',
  discord = 'discord',
  facebook = 'facebook'
}

export type ChatsNameCaptionReplyTime =
  | 'Typically replies instantly'
  | 'Typically replies in minutes'
  | 'Typically replies within an hour'
  | 'Typically replies in a few hours'
  | 'Typically replies within a day';

export type ChatsPosition = 'floating' | 'embed-bubble' | 'embed-chat';

export type ChatsDisplayPage = 'allPages' | 'excludedPages' | 'specificPages';
export type ChatsNotification = 'tabTitle' | 'bubbleBadge';
type ChatsVisibilityPage = { id: string; url: string };

type ChatsVisitors = 'allVisitors' | 'newVisitors' | 'returningVisitors';

export enum ChatsChannelType {
  whatsapp = 'whatsApp',
  email = 'email',
  call = 'call',
  instagram = 'instagram',
  telegram = 'telegram',
  facebook = 'facebook',
  custom = 'custom',
  getdirection = 'getDirections',
  kakaotalk = 'kakaoTalk',
  line = 'line',
  googlemaps = 'googleMaps',
  savecontact = 'saveContact',
  sms = 'SMS',
  snapchat = 'snapchat',
  viber = 'viber',
  twitter = 'twitter',
  discord = 'discord',
  wechat = 'weChat',
  rumbletalk = 'rumbleTalk'
}

export type ChatsFacebookPage = {
  identity: string;
  status: string;
  data: {
    id: string;
    name: string;
    link: string;
    pictureURL: string;
  };
};

export type ChatsChannelBase = {
  type: ChatsChannelType;

  whatsappPhone?: string;
  email?: string;
  callNumber?: string;
  instagramAccountId?: string;
  telegramLink?: string;
  facebookPage?: ChatsFacebookPage;
  customButtonText?: string;
  customButtonLink?: LinkValue;
  customButtonIcon?: ImageValue;
  getDirections?: string;
  kakaoTalkId?: string;
  lineAccountId?: string;
  googleMaps?: string;
  saveContactFullName?: string;
  saveContactJobTitle?: string;
  saveContactCompany?: string;
  saveContactPhoneNumber?: string;
  saveContactEmail?: string;
  saveContactWebSite?: string;
  saveContactAddress?: string;
  saveContactImage?: ImageValue;
  sms?: string;
  snapchatUsername?: string;
  viberPhone?: string;
  weChatAccountId?: string;
  twitterUsername?: string;
  discordAccountId?: string;
  rumbletalkChatId?: string;
};

export type ChatsVCardSetting = {
  name: string;
  job: string;
  company: string;
  image: string;
  phone: string;
  address: string;
  email: string;
  website: string;
};

export type ChatsChannelSetting = {
  color?: string;
  icon?: { url: string };
  withCaption?: boolean;
  link?: {
    url: string;
    fallbackUrl?: string;
    vcardSettings?: ChatsVCardSetting;
    options?: {
      withFeatures?: boolean;
      openCurrentPage?: boolean;
      target: string;
    };
  };
  provider?: ChatsChannelType;
  title?: string;
};

export type ChatsChannelCollection = CollectionItem<ChatsChannelBase>;

export type ChatsNameCaption = 'replyTime' | 'online' | 'custom';
export type ChatsChatMethod = 'startChat' | 'sendMessage';

export type GoogleAnalyticsEventValueType =
  | 'widgetOpened'
  | 'buttonClicked'
  | 'chatStarted';
export type AllInOneChatEventParameterType = 'buttonType';

export type ChatSettings = WidgetSettings<{
  name: string;
  nameCaptionType: ChatsNameCaption;
  nameCaptionReplyTime: ChatsNameCaptionReplyTime;
  nameCaptionCustom: string;
  align: AlignmentValue;
  bubbleText: string;
  bubbleIcon: ImageValue;
  welcomeMessage: string;
  buttonIconVisible: boolean;
  position: ChatsPosition;
  whereToDisplayChat: ChatsDisplayPage;
  excludedPages: ChatsVisibilityPage[];
  specificPages: ChatsVisibilityPage[];
  timeOnPage: number;
  scrollPosition: number;
  onPageLoad: boolean;
  displayDays: (1 | 2 | 3 | 4 | 5 | 6 | 0)[]; // 0-6, где 0 - воскресенье
  timeSchedule: boolean;
  displayStartTime: DateTimeValue<'time'>;
  displayEndTime: DateTimeValue<'time'>;
  rememberVisitor: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 14 | 30;
  buttonText: string;
  windowWidth: string;
  devicesToDisplay: ('mobile' | 'desktop')[];
  showChatTo: ChatsVisitors;
  timeOnSite: number;
  exitIntent: boolean;
  notifications: ChatsNotification[];
  buttonBorderRadius: number;
  bubbleAnimationInterval: number;
  bubbleAnimationEnabled: boolean;
  messageBackgroundColor: string;
  messageFieldColor: string;
  messageButtonColor: string | MulticolorValue;
  messagePlaceholder: string;
  chatWallpaper: string;
  chatWallpaperMulticolor: MulticolorValue;
  buttonsColor: MulticolorValue;
  bubbleBackgroundColor: string;
  bubbleIconColor: string;
  headerBackgroundColor: string;
  notificationBadgeColor: string;
  userOnlinePoint: string;
  customCSS: string;
  customJS: string;
  floatingVerticalOffset: number;
  floatingHorizontalOffset: number;
  customPicture: ImageValue;
  font: FontValue;
  googleAnalytics: GoogleAnalyticsEventValue<
    GoogleAnalyticsEventValueType,
    AllInOneChatEventParameterType
  >[];
  authFacebook?: FBPageValue;
  infoFromFacebook?: boolean;
  chatMethod?: ChatsChatMethod;

  channels: ChatsChannelCollection[];
}>;
